import React, { FC, HTMLAttributes } from 'react';
import styles from './Slider.module.scss';
import { currencyHandler } from '@/helpers/currencyHandler';
interface SliderProps extends Omit<HTMLAttributes<HTMLInputElement>, 'onChange'> {
  value: number | number[];
  onChange: (newValue: number | number[]) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  data_cy?: string;
}
const Slider: FC<SliderProps> = ({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  disabled = false,
  data_cy,
  className = '',
  ...props
}) => {
  const isRange = Array.isArray(value);
  const singleValue = typeof value === 'number' ? value : value?.[0];
  const handleSingleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(e.target.value);
    onChange(newValue);
  };
  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Math.min(Number(e.target.value), value[1] - 1);
    onChange([newValue, value[1]]);
  };
  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Math.max(Number(e.target.value), value[0] + 1);
    onChange([value[0], newValue]);
  };
  const minPercent = (isRange ? value[0] : singleValue - min) / (max - min) * 100;
  const maxPercent = isRange ? (value[1] - min) / (max - min) * 100 : minPercent;
  const handleClickOnTrack = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const track = e.currentTarget;
    const rect = track.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickPercent = clickX / rect.width * 100;
    let clickValue = min + (max - min) * clickPercent / 100;
    clickValue = Math.round(clickValue / step) * step;
    if (isRange) {
      const isCloserToMin = Math.abs(clickValue - value[0]) < Math.abs(clickValue - value[1]);
      if (isCloserToMin) {
        onChange([Math.min(clickValue, value[1] - step), value[1]]);
      } else {
        onChange([value[0], Math.max(clickValue, value[0] + step)]);
      }
    } else {
      onChange(clickValue);
    }
  };
  return <div className={`${styles['slider-container']} ${className}`} data-cy={data_cy} {...props} data-sentry-component="Slider" data-sentry-source-file="Slider.tsx">
      <div className={`${styles['slider-track']} ${disabled ? styles['disabled-track'] : ''}`} style={{
      background: disabled ? `linear-gradient(to right, #f0efff 100%)` : isRange ? `linear-gradient(to right, #f0efff ${minPercent}%, #4756b4 ${minPercent}%, #4756b4 ${maxPercent}%, #f0efff ${maxPercent}%)` : `linear-gradient(to right, #4756b4 ${minPercent}%, #f0efff ${minPercent}%)`
    }} onClick={handleClickOnTrack} />
      <span>
        <input type="range" className={`${styles['thumb']} ${disabled ? styles['disabled-thumb'] : ''} `} min={min} max={max} step={step || 1} value={isRange ? value[0] : singleValue} onChange={isRange ? handleMinChange : handleSingleChange} disabled={disabled} />
        <span className={styles['tooltip-left']} style={{
        left: `calc(${minPercent}% + ${8 - minPercent * 0.2}px)`
      }}>
          {isRange ? currencyHandler(value[0]) : currencyHandler(singleValue)}
        </span>
      </span>

      {isRange && <span>
          <input type="range" className={`${styles['thumb']} ${disabled ? styles['disabled-thumb'] : ''} `} min={min} max={max} step={step || 1} value={value[1]} onChange={handleMaxChange} disabled={disabled} />
          <span className={styles['tooltip-right']} style={{
        right: `calc(100% - calc(${maxPercent}% + ${8 - maxPercent * 0.2}px))`
      }}>
            {currencyHandler(value[1])}
          </span>
        </span>}
    </div>;
};
export default Slider;